import { API_CONFIG, API_ENDPOINTS } from "../config/api.config";
import { clearTokens, getStoredRefreshToken, getStoredToken, saveTokens } from "../utils/auth";

export class HttpError extends Error {
  status: number;
  data?: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
  }
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) return false;

      const response = await fetch(API_ENDPOINTS.auth.refresh, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (data.accessToken && data.refreshToken) {
        saveTokens(data.accessToken, data.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function handleErrorResponse(response: Response): Promise<HttpError> {
  let message = `HTTP ${response.status}`;
  let errorData: any = null;

  try {
    errorData = await response.json();
    message = errorData?.message || errorData?.error || message;
  } catch {
    // non-JSON body
  }

  switch (response.status) {
    case 403:
      if (!errorData?.message) message = "You do not have permission to perform this action";
      break;
    case 404:
      if (!errorData?.message) message = "The requested resource was not found";
      break;
    case 500:
      if (!errorData?.message) message = "An internal server error occurred";
      break;
    case 502:
      message = "Bad gateway — server temporarily unavailable";
      break;
    case 503:
      message = "Service unavailable — server temporarily overloaded";
      break;
    case 504:
      message = "Gateway timeout — server took too long to respond";
      break;
  }

  return new HttpError(response.status, message, errorData);
}

async function request<T>(url: string, options: RequestInit & { timeout?: number } = {}): Promise<T> {
  const { timeout = API_CONFIG.timeout, ...fetchOptions } = options as any;
  const fullUrl = url.startsWith("http") ? url : url;

  const token = getStoredToken();
  const isFormData = fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(fetchOptions.headers as Record<string, string> || {}),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (
        response.status === 401 &&
        !url.includes("/auth/refresh") &&
        !url.includes("/auth/login")
      ) {
        const refreshed = await attemptTokenRefresh();
        if (refreshed) {
          const retryToken = getStoredToken();
          const retryResponse = await fetch(fullUrl, {
            ...fetchOptions,
            headers: {
              ...headers,
              ...(retryToken ? { Authorization: `Bearer ${retryToken}` } : {}),
            },
          });
          if (retryResponse.ok) return handleSuccess<T>(retryResponse);
          throw await handleErrorResponse(retryResponse);
        }
        clearTokens();
        window.location.href = "/login";
        throw new HttpError(401, "Session expired");
      }

      if (response.status === 401) {
        throw await handleErrorResponse(response);
      }

      throw await handleErrorResponse(response);
    }

    return handleSuccess<T>(response);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof HttpError) throw err;
    if (err instanceof Error && err.name === "AbortError") {
      throw new HttpError(408, "The request took too long to complete");
    }
    if (err instanceof Error && err.message.includes("Failed to fetch")) {
      throw new HttpError(0, "Unable to connect to the server");
    }
    throw new HttpError(0, err instanceof Error ? err.message : "Unknown error");
  }
}

async function handleSuccess<T>(response: Response): Promise<T> {
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0" || response.status === 204) return {} as T;
  try {
    return await response.json();
  } catch {
    return {} as T;
  }
}

export const http = {
  get: <T>(url: string, options?: RequestInit) => request<T>(url, { ...options, method: "GET" }),
  post: <T>(url: string, data?: any, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: "POST",
      body: data instanceof FormData ? data : data != null ? JSON.stringify(data) : undefined,
    }),
  patch: <T>(url: string, data?: any, options?: RequestInit) =>
    request<T>(url, {
      ...options,
      method: "PATCH",
      body: data != null ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(url: string, options?: RequestInit) =>
    request<T>(url, { ...options, method: "DELETE" }),
};
